<template>
  <bm-dialog
    :label="dialogLabels.title"
    @accept="validateAndAccept"
    @cancel="cancel"
  >
    <bm-alert v-if="visitIsOnActiveJourney" type="warning" dismissible>
      {{ $m.distribution_visit_on_active_journey }}
    </bm-alert>
    <v-container pa-0 fluid>
      <v-row>
        <v-col class="pb-0" cols="6">
          <bm-dropdown
            :value.sync="selectedBusinessId"
            :items="allBusinesses"
            :t="$m.keys.distribution_outlet_supplier"
            item-text="businessName"
            item-value="id"
            :rules="[validOutletSupplierOrAddressRule]"
            :disabled="!!ullageHeader"
            data-qa="add_visit_select_business"
            clearable
            @update:value="selectBusiness"
          />
        </v-col>
        <v-col class="pb-0" cols="6">
          <bm-date-q
            :value.sync="visit.date"
            :t="$m.keys.date"
            :date-events="isValidDeliveryEvent"
            :rules="[
              $ruleSet.required(),
              $ruleSet.dateAfterOrEqual(today, $m.today),
            ]"
          />
        </v-col>
        <v-col class="pt-0" cols="12">
          <bm-btn-expand
            :value.sync="showAddress"
            :more-text-override="$m.distribution_manage_address"
            block
          />
        </v-col>
      </v-row>
    </v-container>

    <!-- Address Section -->
    <v-slide-x-transition>
      <bm-form-flow-layout v-if="showAddress" cols="12" sm="6">
        <bm-text
          :value.sync="visit.businessName"
          :t="$m.keys.distribution_business_name"
          :rules="[validOutletSupplierOrAddressRule]"
        />
        <bm-text
          :value.sync="visit.contactName"
          :t="$m.keys.distribution_contact"
        />

        <bm-address
          :t="$m.keys.address"
          :address-line1.sync="visit.addressLine1"
          :address-line2.sync="visit.addressLine2"
          :address-line3.sync="visit.addressLine3"
          :address-line4.sync="visit.addressLine4"
          :address-line5.sync="visit.addressLine5"
          :address-line6.sync="visit.addressLine6"
          :address-postcode.sync="visit.addressPostcode"
        />
        <bm-telephone
          :value.sync="visit.telephoneNumber"
          :t="$m.keys.distribution_contact_telephone"
        />
      </bm-form-flow-layout>
    </v-slide-x-transition>

    <bm-form-flow-layout cols="6">
      <bm-time
        :value.sync="visit.startTime"
        :t="dialogLabels.timeFrom"
        :error-messages="deliveryTimeErrors"
        data-qa="add_visit_start_time"
        clearable
      />
      <bm-time
        :value.sync="visit.endTime"
        :t="dialogLabels.timeTo"
        data-qa="add_visit_end_time"
        clearable
      />
      <bm-dropdown
        v-if="showVehicleMenu"
        :value.sync="selectedVehicleId"
        :items="activeVehiclesToday"
        :t="$m.keys.distribution_vehicle"
        item-text="name"
        item-value="id"
        clearable
      />
      <bm-number-q
        :value.sync="visit.stopMinutes"
        :t="$m.keys.distribution_stop_time"
        :rules="[$ruleSet.notNegativeNumber()]"
        :min="0"
        integral
      />
      <bm-weight
        :value.sync="visit.weightToDeliverKg"
        :t="$m.keys.distribution_visit_weight"
      />
    </bm-form-flow-layout>
    <v-container fluid pa-0>
      <v-row>
        <v-col cols="12">
          <bm-text-area
            :value.sync="visit.reason"
            :t="dialogLabels.textArea"
            data-qa="add_visit_reason"
          />
        </v-col>
      </v-row>
    </v-container>
  </bm-dialog>
</template>

<script setup lang="ts">
import BmBtnExpand from "@/components/controls/input/BmBtnExpand.vue";
import BmDialog from "@/components/dialogs/BmDialog.vue";
import BmAlert from "@/components/controls/BmAlert.vue";
import BmTelephone from "@/components/controls/input/specialised/BmTelephone.vue";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import { watch, onBeforeMount, computed } from "vue";
import type { LocalDate } from "@js-joda/core";
import type { Vehicle } from "@/app/services/generated/vehicleService";
import type { UllageHeader } from "@/app/services/generated/ullageService";
import { Visit } from "@/app/services/generated/visitService";
import { getById, isNotNullOrUndefined } from "@/app/apiutils";
import {
  DistributionConfiguration,
  distributionConfigurationService,
} from "@/app/services/generated/distributionconfigurationService";
import type { Outlet } from "@/app/services/generated/outletService";
import { outletService } from "@/app/services/generated/outletService";
import {
  makeDeepRef,
  makeRef,
  withLoading,
} from "@/components/composables/componentTools";
import Guid from "@/app/types/common/guid";
import { journeyService as journeyServiceV3 } from "@/app/services/extensions/journeyServiceV3Extended";
import { isJourneyV3InProgress } from "@/business_logic/distribution/journeyUtils";
import { useI18n } from "@/plugins/i18n";
import type { DialogEvents } from "@/components/mixins/dialogs";
import { useReactiveDialogScriptSetup } from "@/components/mixins/dialogs";
import type { ValidationRule } from "@/plugins/validation";
import { ruleSet } from "@/plugins/validation";
import { despatchDateHighlighter } from "@/components/controls/input/mixins/BmDateHighlighter";
import BmAddress from "@/components/controls/address/BmAddress.vue";
import { buildCache } from "@/app/caching/buildCache";

interface Business {
  id: Guid;
  businessName: string;
  isHidden: boolean;
}

export interface AddVisitEvent {
  visit: Visit;
  vehicleId: Guid | null;
}

interface Props {
  date?: LocalDate | null;
  vehicle?: Vehicle | null;
  ullageHeader?: UllageHeader | null;
  activeVehiclesToday?: readonly Vehicle[];
  visitToEdit?: Visit | null;
  businessId?: Guid | null;
  reason?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  activeVehiclesToday: () => [],
});

interface Emits extends DialogEvents {}
const emits = defineEmits<Emits>();

const { accept, cancel } = useReactiveDialogScriptSetup(emits);

const { $m } = useI18n();
const visit = makeDeepRef<Visit>(props.visitToEdit?.clone() ?? new Visit());
const distributionConfiguration = makeRef(new DistributionConfiguration());
const selectedVehicleId = makeRef(props.vehicle?.id ?? null);
const selectedBusinessId = makeRef(props.visitToEdit?.outletId ?? null);
const deliveryTimeErrors = makeRef<readonly string[]>([]);
const inProgressVisitIds = makeRef<readonly Guid[]>([]);
const outlets = makeRef<readonly Outlet[]>([]);
const cache = buildCache()
  .withDeliveryAreas()
  .withSupplierManufacturers()
  .withLeads()
  .build();
const showAddress = makeRef(!visit.value.outletId && !!visit.value.version);
const allBusinesses = computed((): Business[] =>
  outlets.value
    .map(
      (outlet): Business => ({
        id: outlet.id,
        businessName: outlet.displayName,
        isHidden: outlet.isHidden,
      })
    )
    .concat(cache.supplierManufacturers.allReactive.value)
    .concat(cache.leads.all)
);

const loadPrerequisites = withLoading(async () => {
  [outlets.value, distributionConfiguration.value] = await Promise.all([
    outletService.getAllOutlets(),
    distributionConfigurationService.getDistributionConfiguration(),
    cache.update(),
  ]);

  if (props.visitToEdit) {
    inProgressVisitIds.value = (
      await journeyServiceV3.getJourneyDetailsForDespatchDate(
        tenantService.today
      )
    )
      .filter((journey) => isJourneyV3InProgress(journey))
      .flatMap((journey) => journey.journey.stopKeys)
      .map((stop) => stop.visitId)
      .filter(isNotNullOrUndefined);
  }
});

const selectBusiness = (id: Guid | null) => {
  // In the case of clearing
  if (id === null) {
    visit.value.outletId = null;
    return;
  }

  const outlet = getById(outlets.value, id);

  // If what we selected is an outlet, set it as our outlet and update the address
  if (outlet) {
    visit.value.outletId = outlet.id;
    if (outlet.hasDefaultDeliveryAddress) {
      visit.value.businessName = outlet.displayName;
      visit.value.addressLine1 = outlet.defaultDeliveryAddressLine1;
      visit.value.addressLine2 = outlet.defaultDeliveryAddressLine2;
      visit.value.addressLine3 = outlet.defaultDeliveryAddressLine3;
      visit.value.addressLine4 = outlet.defaultDeliveryAddressLine4;
      visit.value.addressLine5 = outlet.defaultDeliveryAddressLine5;
      visit.value.addressLine6 = outlet.defaultDeliveryAddressLine6;
      visit.value.addressPostcode = outlet.defaultDeliveryAddressPostcode;
    } else {
      visit.value.businessName = outlet.displayName;
      visit.value.addressLine1 = outlet.mainAddressLine1;
      visit.value.addressLine2 = outlet.mainAddressLine2;
      visit.value.addressLine3 = outlet.mainAddressLine3;
      visit.value.addressLine4 = outlet.mainAddressLine4;
      visit.value.addressLine5 = outlet.mainAddressLine5;
      visit.value.addressLine6 = outlet.mainAddressLine6;
      visit.value.addressPostcode = outlet.mainAddressPostcode;
    }
    visit.value.contactName = outlet.defaultDeliveryRecipient;
    visit.value.telephoneNumber =
      outlet.defaultDeliveryTelephoneNumber || outlet.telephoneNumber;
    visit.value.startTime = outlet.deliveryTimeStart;
    visit.value.endTime = outlet.deliveryTimeEnd;
    return;
  }

  // Is it a supplier
  const supplier = cache.supplierManufacturers.getById(id);
  if (supplier) {
    visit.value.outletId = null;
    visit.value.businessName = supplier.businessName;
    visit.value.addressLine1 = supplier.addressLine1;
    visit.value.addressLine2 = supplier.addressLine2;
    visit.value.addressLine3 = supplier.addressLine3;
    visit.value.addressLine4 = supplier.addressLine4;
    visit.value.addressLine5 = supplier.addressLine5;
    visit.value.addressLine6 = supplier.addressLine6;
    visit.value.addressPostcode = supplier.postcode;
    visit.value.telephoneNumber = supplier.phoneNumber;
    visit.value.contactName = supplier.contactName;
    return;
  }

  // Otherwise we should have a lead
  const lead = cache.leads.getById(id);
  if (lead) {
    visit.value.outletId = null;
    visit.value.businessName = lead.businessName;
    visit.value.addressLine1 = lead.addressLine1;
    visit.value.addressLine2 = lead.addressLine2;
    visit.value.addressLine3 = lead.addressLine3;
    visit.value.addressLine4 = lead.addressLine4;
    visit.value.addressLine5 = lead.addressLine5;
    visit.value.addressLine6 = lead.addressLine6;
    visit.value.addressPostcode = lead.addressPostcode;
    visit.value.telephoneNumber = lead.telephoneNumber;
    visit.value.contactName = lead.contactName;
    return;
  }
};

const validateAndAccept = () => {
  if (visit.value.version === 0) {
    visit.value.id = Guid.create();
  }

  const isVehicleAvailable = props.activeVehiclesToday.some((vehicle) =>
    vehicle.id.equals(selectedVehicleId.value)
  );

  // Remove the vehicle if it's not available, or we aren't looking at today
  if (!isVehicleAvailable || !tenantService.today.equals(visit.value.date)) {
    selectedVehicleId.value = null;
  }

  accept({
    visit: visit.value,
    vehicleId: selectedVehicleId.value,
  } as AddVisitEvent);
};

onBeforeMount(async () => {
  await loadPrerequisites();

  // Edit / duplicate: visit already cloned from visitToEdit — leave it alone.
  if (props.visitToEdit) {
    selectedBusinessId.value = props.visitToEdit.outletId;
    return;
  }

  // Create: hydrate a fresh Visit from props (and optional ullage overlay).
  visit.value.date = props.date ?? tenantService.today;
  visit.value.reason = props.reason ?? "";
  visit.value.stopMinutes = distributionConfiguration.value.defaultStopMinutes;

  if (props.ullageHeader) {
    selectedBusinessId.value = props.ullageHeader.outletId;
    visit.value.date = props.ullageHeader.pickupDate ?? tenantService.today;
    visit.value.startTime = props.ullageHeader.pickupWindowStart;
    visit.value.endTime = props.ullageHeader.pickupWindowEnd;
    visit.value.stopMinutes = props.ullageHeader.pickupStopMinutes;
    visit.value.reason = props.ullageHeader.pickupNotesForDriver;
    selectBusiness(props.ullageHeader.outletId!);
  }

  if (props.businessId) {
    selectedBusinessId.value = props.businessId;
    selectBusiness(props.businessId);
  }
});

const dialogLabels = computed(() => {
  if (props.ullageHeader) {
    return {
      title: $m.ullages_add_pickup,
      textArea: $m.keys.ullages_notes_to_driver,
      timeFrom: $m.keys.ullages_pickup_time_from,
      timeTo: $m.keys.ullages_pickup_time_to,
    };
  } else {
    return {
      title: props.visitToEdit
        ? $m.distribution_edit_visit
        : $m.distribution_add_visit,
      textArea: $m.keys.distribution_reason,
      timeFrom: $m.keys.distribution_visit_time_from,
      timeTo: $m.keys.distribution_visit_time_to,
    };
  }
});

const showVehicleMenu = computed(() => {
  return (
    visit.value.date.equals(tenantService.today) &&
    props.activeVehiclesToday.length
  );
});

const visitIsOnActiveJourney = computed(() => {
  return inProgressVisitIds.value.some((id) => id.equals(visit.value.id));
});

const validOutletSupplierOrAddressRule = computed((): ValidationRule => {
  const validAddress =
    visit.value.businessName &&
    visit.value.addressLine1 &&
    visit.value.addressPostcode;

  return validAddress || visit.value.outletId
    ? () => true
    : ruleSet.required(true, $m.keys.distribution_outlet_supplier_address_rule);
});

const deliveryTimeChange = () => {
  // If both values exist and there is a mismatch (start is after end) add error
  if (
    visit.value.endTime &&
    visit.value.startTime?.isAfter(visit.value.endTime)
  ) {
    deliveryTimeErrors.value = [$m.distribution_delivery_times_mismatch];
  }
  // XOR to check that only one of the values is null/ undefined
  else if (
    visit.value.endTime ? !visit.value.startTime : visit.value.startTime
  ) {
    deliveryTimeErrors.value = [$m.distribution_delivery_times_mismatch_empty];
  } else {
    deliveryTimeErrors.value = [];
  }
};

const isValidDeliveryEvent = (date: LocalDate) => {
  const outletDeliveryArea = getById(
    outlets.value,
    visit.value.outletId
  )?.deliveryAreaId;

  const deliveryArea = cache.deliveryAreas.getById(outletDeliveryArea);

  return deliveryArea
    ? despatchDateHighlighter(
        date,
        deliveryArea.despatchDays,
        0,
        deliveryArea.repeatWeeks,
        deliveryArea.repeatStartDate
      )
    : "";
};

const today = tenantService.today;

watch(() => [visit.value.startTime, visit.value.endTime], deliveryTimeChange);
</script>
